import { MAINTENANCE_LABELS, type MaintenanceCategory } from '@garage/shared';
import { frDecimal } from '../lib/decimal.js';
import { KIND_LABELS } from '../lib/format.js';
import { Money, hasAmount } from '../ui/money.js';

/** Ligne d'un complément ou d'un travail réalisé, telle que renvoyée par l'API. */
export interface DisplayLine {
  key: string;
  kind: string;
  label: string;
  reference: string | null;
  quantity: string;
  maintenanceCategory?: MaintenanceCategory | null;
  unitPriceHt?: string | null;
  totalTtc?: string | null;
}

/**
 * Liste de lignes, lisible sur téléphone (pas de tableau à faire défiler). Chaque montant n'est
 * affiché que s'il a été renvoyé par l'API ; aucun total n'est recalculé ici. Chaque montant de
 * ligne est marqué « TTC » (aucune ambiguïté HT/TTC) ; le prix unitaire HT n'est montré qu'au garage
 * (`showUnitPrice`), jamais au client.
 */
export function LinesList({
  lines,
  totalTtc,
  totalLabel = 'Total TTC',
  showUnitPrice = true,
}: {
  lines: DisplayLine[];
  totalTtc?: string | null;
  totalLabel?: string;
  showUnitPrice?: boolean;
}) {
  return (
    <div>
      <ul className="divide-y divide-zinc-100">
        {lines.map((l) => (
          <li key={l.key} className="flex items-start justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="font-medium text-zinc-900">{l.label}</p>
              <p className="text-sm text-zinc-500">
                {[
                  KIND_LABELS[l.kind] ?? l.kind,
                  l.maintenanceCategory ? MAINTENANCE_LABELS[l.maintenanceCategory] : null,
                  l.reference ? `Réf. ${l.reference}` : null,
                  `Qté ${frDecimal(l.quantity)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {showUnitPrice && hasAmount(l.unitPriceHt) ? (
                  <>
                    {' · '}
                    <Money value={l.unitPriceHt} suffix="HT l’unité" />
                  </>
                ) : null}
              </p>
            </div>
            <Money value={l.totalTtc} suffix="TTC" className="shrink-0 whitespace-nowrap font-medium" />
          </li>
        ))}
      </ul>
      {hasAmount(totalTtc) ? (
        <div className="mt-2 flex items-baseline justify-between border-t border-zinc-200 pt-2">
          <span className="font-medium">{totalLabel}</span>
          <Money value={totalTtc} className="text-lg font-semibold" />
        </div>
      ) : null}
    </div>
  );
}
